/**
 * The kit's shared vocabulary.
 *
 * Every fixed value set in `Cresultcard` is an enum declared here, so that a
 * pillar author picks a member rather than inventing a string, and so that a
 * new pillar cannot widen a closed set by accident. Sizes, gaps and radii are
 * steps on named scales for the same reason: eighteen families choosing their
 * own pixel values is exactly how eighteen designers' work happens.
 */
import type { ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";

/* ── Surface and rhythm ───────────────────────────────────────────────────── */

/** Which composition a shell is rendering. It decides region behaviour, never size. */
export enum CresultcardDensity {
  /** Deck card: `auto 1fr auto`, so siblings reach equal height without a floor. */
  Rail = "rail",
  /** Standalone card: content height, always. */
  Card = "card",
  /** The detail tier's main column. */
  Detail = "detail",
}

/**
 * Data-semantic colour. It carries meaning and is never decorative: a pillar
 * does not get a colour, a FACT does. Mint is the system's only brand hue.
 */
export enum CresultcardTone {
  /** Live, positive, in-stock, gain, confirmed. */
  Positive = "positive",
  /** Loss, sold out, expired, closed, price down. */
  Negative = "negative",
  /** Clamped, degraded, estimated, sponsored, unverified. */
  Caution = "caution",
  /** Ordinary value text. */
  Neutral = "neutral",
  /** Supporting or derived value text. */
  Muted = "muted",
}

/** The closed type scale. Size is a step on it, never a raw pixel prop. */
export enum CresultcardTextStep {
  Focal = "focal",
  Lead = "lead",
  Title = "title",
  Body = "body",
  Meta = "meta",
  Eyebrow = "eyebrow",
}

/** The figure ramp. A composition picks its step; nobody passes a pixel size. */
export enum CresultcardFigureScale {
  Row = "row",
  Rail = "rail",
  Card = "card",
  Detail = "detail",
  Focal = "focal",
}

/** The four-step vertical rhythm. There is no arbitrary gap number. */
export enum CresultcardStackDensity {
  Tight = "tight",
  Snug = "snug",
  Regular = "regular",
  Loose = "loose",
}

/** The three-step inset scale for a tile's own padding. */
export enum CresultcardTilePadding {
  None = "none",
  Snug = "snug",
  Regular = "regular",
}

/** A tile's surface treatment. Both are dark; neither is a theme. */
export enum CresultcardTileTone {
  /** Raised off the plate — the default block. */
  Raised = "raised",
  /** Sunk into it — a well for a ledger body or an offer row. */
  Well = "well",
}

/** Where a set's items are laid out. */
export enum CresultcardLayout {
  /** One bounded widget: header, N dense rows. */
  Rows = "rows",
  /** One widget whose body is a horizontally scrolled deck. */
  Rail = "rail",
  /** N independent cards in the document flow, each its own object. */
  Cards = "cards",
  /**
   * The first item alone, as its rail card, with no set chrome at all — the
   * face an `attach` fence puts in a text line. It drills nowhere of its own:
   * the open handle arrives through `CresultcardInlineOpenContext`, and the
   * host that provides it draws the pane wherever the line allows.
   */
  Inline = "inline",
  /**
   * The first item's expanded pane inside the tray, the set header above it,
   * no list and no drill-back — what an opened inline card, or the result
   * panel, shows. A family with no expanded composition shows its card.
   */
  Detail = "detail",
}

/** Start / end only. No component in the kit names a physical side. */
export enum CresultcardAlign {
  Start = "start",
  End = "end",
}

/** The four corners of a media frame, in logical terms. */
export enum CresultcardCorner {
  TopStart = "topStart",
  TopEnd = "topEnd",
  BottomStart = "bottomStart",
  BottomEnd = "bottomEnd",
}

/**
 * How far out of the media frame an overlay corner sits. `Straddle` centres the
 * corner content on the frame's own block edge, which is the only way a date
 * plate or a dock rides that edge without a pillar hand-lifting it with a
 * negative margin against a padding value it does not own.
 */
export enum CresultcardOverlayReach {
  Inside = "inside",
  Straddle = "straddle",
}

/**
 * A lane's position on the accent ramp. Closed by design: the system has one
 * brand colour, and a lane rule varies its weight, never its hue.
 */
export enum CresultcardLaneSlot {
  Primary = "primary",
  Secondary = "secondary",
  Tertiary = "tertiary",
  Quaternary = "quaternary",
  /**
   * The fifth position. It exists because a pillar answering from five legal
   * systems had to give two of them one weight, and a ramp that runs out is a
   * ramp that makes a pillar collide two lanes it can otherwise tell apart.
   */
  Quinary = "quinary",
}

/**
 * Where the clearance beside a lane rule comes from.
 *
 * The rule itself is 2px and out of flow in both readings; what costs inline
 * space is the gutter the wrapped content is held clear of it by. `Reserved`
 * makes the lane manufacture that gutter, which comes out of the wrapped
 * element's own width — and a `CresultcardRow` is measured at 548px (§3.3),
 * a contract those pixels are not budgeted in. `Holder` puts the rule in the
 * inline padding the wrapped element already carries (a row's is 13px), so the
 * content keeps its full measure and the rule is drawn in exactly the same
 * place.
 *
 * The choice is therefore per COMPOSITION, never per family: it is a claim about
 * the one element the lane wraps at that site, and a family whose row hands over
 * a padded row hands over a bare stack on its cards. `Holder` where the wrapped
 * element carries no inline padding paints the rule straight over the content's
 * leading edge, which is why `Reserved` is the default — it is the only reading
 * that is safe without looking at the child, and the lane cannot look.
 */
export enum CresultcardLaneClearance {
  Reserved = "reserved",
  Holder = "holder",
}

/**
 * The floor under a row's identity zone.
 *
 * The identity is the only zone allowed to shrink, which means a wide trailing
 * zone can take it all the way down to nothing — the item's own name reduced to
 * an ellipsis while a figure keeps its full width. `None` is the default and is
 * exactly `minmax(0, 1fr)`: a row that has never needed a floor does not
 * acquire one.
 */
export enum CresultcardRowIdentityFloor {
  None = "none",
  /** Enough for a title line to stay a title line. */
  Title = "title",
  /** Enough for a title and a meta line that is itself scanned. */
  Wide = "wide",
}

/**
 * How a row's three zones sit on the cross axis.
 *
 * `Center` is the row every family already renders. `Stretch` is what lets a
 * lead ornament SPAN a two-line row — a rule, a bracket or a mark that belongs
 * to both lines rather than to the space between them, which centring makes
 * impossible to draw.
 */
export enum CresultcardRowZoneAlign {
  Center = "center",
  Stretch = "stretch",
}

/* ── Identity and text ────────────────────────────────────────────────────── */

/**
 * Clamping is an explicit decision, so `Full` is a member rather than the
 * absence of a value. Detail and expanded compositions may only pass `Full`.
 */
export enum CresultcardTitleLines {
  One = "one",
  Two = "two",
  Three = "three",
  Full = "full",
}

/** The two-step subline scale. */
export enum CresultcardSublineSize {
  Small = "small",
  Regular = "regular",
}

/**
 * Whether an eyebrow is one of OUR labels or the vendor's own captured text.
 *
 * The eyebrow type step uppercases, which is exactly right for a kit label —
 * `NOW PLAYING`, `SPONSORED` — and destroys anything captured. A ticker, a
 * product line, a court's own style or a gene symbol carries case as data, and
 * `text-transform` throws it away with no way to get it back, because the
 * transform happens at paint and the string a reader sees is not the string the
 * vendor sent.
 */
export enum CresultcardEyebrowVoice {
  Label = "label",
  Captured = "captured",
}

/** The fields an identity block can yield, named so a caller can order them. */
export enum CresultcardIdentitySlot {
  Eyebrow = "eyebrow",
  Title = "title",
  Secondary = "secondary",
  Trailing = "trailing",
}

/** How much of a source's identity is shown. */
export enum CresultcardIdentityBandDensity {
  /** Mark plus name. */
  Inline = "inline",
  /** Mark, name, secondary and age. */
  Byline = "byline",
  /** The full profile band: eyebrow strip, overlapping mark, stats row. */
  Banner = "banner",
}

/** Where an identity strip sits against the media frame it belongs to. */
export enum CresultcardStripPlacement {
  OverMedia = "overMedia",
  OverlappingMedia = "overlappingMedia",
  BelowMedia = "belowMedia",
}

/* ── Facts, chips and prose ───────────────────────────────────────────────── */

/**
 * Whether a rendered value is short enough to earn a side-by-side placement.
 * The caller declares it because only the caller knows the value's real shape.
 */
export enum CresultcardFactWeight {
  Short = "short",
  Long = "long",
}

/**
 * A fact group is a labelled stack unless the caller declares otherwise, and
 * the only declaration available is a four-up of short same-kind figures.
 * There is no `columns` prop anywhere in the kit — a column count is what
 * produced the defect this replaces.
 *
 * `Stack` lets two short values EARN a pair; `Column` refuses the pair outright.
 * They are separate members because a caller whose values are short but whose
 * reading order is a single column had no way to say so — the only lever was to
 * mislabel every value as `Long`, which is a lie about the data to buy a layout.
 */
export enum CresultcardFactGroupShape {
  /** One column, unless every value is short enough to pair at Wide. */
  Stack = "stack",
  /** One column, always. Width never changes the answer. */
  Column = "column",
  FourUp = "fourUp",
}

/** A chip's visual treatment. */
export enum CresultcardChipVariant {
  /** Hairline pill on the plate: an attribute the item carries. */
  Outline = "outline",
  /** Mint-tinted: the item's own distinguishing fact. */
  Accent = "accent",
  /** Amber-tinted: a caveat the reader must not miss. */
  Caution = "caution",
  /** Red-tinted: a closed, expired, sold-out or falling state. */
  Negative = "negative",
  /** Filled dark: an inert label. */
  Solid = "solid",
}

/**
 * Which controlled vocabulary a chip belongs to. Two vocabularies never share
 * one style, so a reader can tell a vendor amenity from a lane name without
 * reading either.
 */
export enum CresultcardChipRegister {
  /** Vendor-supplied attributes of the item itself. */
  Attribute = "attribute",
  /** Our own lane, corpus or projection names. */
  Lane = "lane",
  /** A state the item is in right now. */
  Status = "status",
  /**
   * A term issued by a naming BODY: a court, a regulator, a standards
   * organisation, a cataloguing scheme.
   *
   * It is a fourth vocabulary rather than a variant of the other three. A
   * Library-of-Congress heading is not an `Attribute` — nobody at the vendor
   * typed it, and its `--` subdivision syntax is the scheme's, not the item's;
   * it is not a `Lane`, because a lane name is OURS and this one is the
   * issuer's; and it is not a `Status`, because it does not change. §8.13
   * requires LoC headings and Open Library contributor tags never to share a
   * register, and with three members the only way to comply was to borrow a
   * `variant` — which is tone, and says nothing about what kind of claim a
   * reader is looking at.
   */
  Authority = "authority",
}

/**
 * Whether a rating's population may be absent. `Required` is the default and
 * the rule: 4.75 on 8 reviews and 4.7 on 14,896 are not comparable, so a score
 * without its count is normally an absence with a reason. `Optional` is a lane
 * declaring that the count does not exist rather than being withheld.
 */
export enum CresultcardRatingPopulation {
  Required = "required",
  Optional = "optional",
}

/** How much of a long body is shown before the disclosure. */
export enum CresultcardProseVisible {
  FirstSentence = "firstSentence",
  FirstParagraph = "firstParagraph",
  Lines = "lines",
}

/** Prose has two jobs, and they are not the same component's default. */
export enum CresultcardProseMode {
  /** A short captured string at heading scale. */
  Display = "display",
  /** A long body at reading measure, bounded by a disclosure. */
  Reading = "reading",
}

/* ── Value and money ──────────────────────────────────────────────────────── */

/** Which end of a range a single bound describes. */
export enum CresultcardMoneyBoundKind {
  AtLeast = "atLeast",
  AtMost = "atMost",
}

/** The one period lexicon. No synonyms, no abbreviations. */
export enum CresultcardPricePeriod {
  Night = "night",
  Year = "year",
  Month = "month",
  Hour = "hour",
  Day = "day",
  Person = "person",
}

/** Whether tax is inside the figure. Data, never a hardcoded footnote. */
export enum CresultcardPriceBasis {
  Inclusive = "inclusive",
  Exclusive = "exclusive",
  Unknown = "unknown",
}

/**
 * How a money block lays its parts out. The caller declares it, because the
 * caller owns the column the block has to fit in.
 */
export enum CresultcardMoneyLayout {
  /** Amount, period and discount on one baseline. */
  Baseline = "baseline",
  /**
   * The large-figure block §8.16 asks for: the figure alone on its own line,
   * the unit line beneath it (`/ night · tax incl.`), the struck original and
   * its discount on a third. A row's price column is ~112px wide, and four
   * parts on one baseline there is either a shrunken figure or an overflow.
   */
  Stacked = "stacked",
}

/**
 * Which direction of change is good here. Declared by the call site, never
 * inferred from the sign: −19% emissions is good, −19% portfolio is not.
 */
export enum CresultcardGoodDirection {
  Up = "up",
  Down = "down",
}

/* ── Media ────────────────────────────────────────────────────────────────── */

/** How large a frame is, relative to its composition. Never a pixel height. */
export enum CresultcardMediaScale {
  Thumb = "thumb",
  Face = "face",
  Hero = "hero",
}

/** How a multi-asset set is paged. */
export enum CresultcardMediaPager {
  None = "none",
  Dots = "dots",
  Filmstrip = "filmstrip",
  /**
   * Both, which is not the same as either: the dots (or the `n / m` they
   * degrade to past six) say where in the set the reader is, and the filmstrip
   * says what the rest of the set contains. §8.4's detail asks for exactly this
   * pair — "a dot pager over the whole image set and the filmstrip beneath" —
   * on a 17-to-38 image set where neither alone is the affordance.
   */
  DotsAndFilmstrip = "dotsAndFilmstrip",
}

/** The asset's own resolve state, reported so a composition can adapt. */
export enum CresultcardMarkAssetState {
  /** No `src` was given: the fallback is the design, not a failure. */
  Absent = "absent",
  Loading = "loading",
  Resolved = "resolved",
  /** The host declined, or answered with a tracking pixel. */
  Failed = "failed",
}

/** An identity mark is a square or a circle; nothing else. */
export enum CresultcardMarkShape {
  Square = "square",
  Circle = "circle",
}

/**
 * The five-step mark scale. `Lead` is the two-line row's lead column — a mark
 * that spans both lines and anchors a second identity beneath the first (§8.17:
 * "the mark grows 22 → 32px and spans both lines"). It sits between the card
 * mark and the band mark because that is exactly what it is.
 */
export enum CresultcardMarkSize {
  Row = "row",
  Card = "card",
  Lead = "lead",
  Band = "band",
  Hero = "hero",
}

/* ── Actions and disclosure ───────────────────────────────────────────────── */

/** What an action is worth on its surface. */
export enum CresultcardActionTone {
  /** The one thing this surface is for. */
  Primary = "primary",
  /** A real alternative route to the same answer. */
  Secondary = "secondary",
  /** An inline, low-weight jump. */
  Quiet = "quiet",
}

/** Where a surface's action bar sits. */
export enum CresultcardActionBarLayout {
  Trailing = "trailing",
  Footer = "footer",
  FullWidth = "fullWidth",
}

/** How an action group lays its control out. */
export enum CresultcardActionGroupLayout {
  Inline = "inline",
  FullWidth = "fullWidth",
}

/** Which way a drill transition is travelling. */
export enum CresultcardDrillDirection {
  Forward = "forward",
  Back = "back",
}

/**
 * Which half of a drill swap a pane is. Both are on screen together for the
 * length of the move: the pane arriving, and the one it replaces leaving in the
 * mirror of that arrival.
 */
export enum CresultcardDrillPhase {
  Enter = "enter",
  Exit = "exit",
}

/**
 * Whether a detail composition brings its own tray and header, or is being
 * mounted inside one that already exists — a set's drill pane being the case
 * that matters. Nesting a tray inside a tray is two bezels around one plate.
 */
export enum CresultcardDetailFrame {
  Standalone = "standalone",
  Embedded = "embedded",
}

/* ── Charts and playback ──────────────────────────────────────────────────── */

/** A sparkline answers shape, never level, at one of three sizes. */
export enum CresultcardSparklineScale {
  Inline = "inline",
  Face = "face",
  Block = "block",
}

/** Whether an area chart tracks the pointer, and how. */
export enum CresultcardCrosshair {
  Off = "off",
  Hover = "hover",
  Pinned = "pinned",
}

/**
 * What a track's gradient is painted ACROSS. `Segment` paints the ramp inside
 * the drawn bar, which is right for a bar whose own extent is the subject;
 * `Domain` paints it across the whole scale and shows only the part the segment
 * covers, which is the only way a set of tracks sharing one domain can share one
 * colour ramp. `Segment` is the default because it is what the kit already did.
 */
export enum CresultcardTrackGradientSpan {
  Segment = "segment",
  Domain = "domain",
}

/** What a measured zero looks like on a track. */
export enum CresultcardTrackZeroPolicy {
  /** A labelled baseline tick: the value was measured and it is zero. */
  Tick = "tick",
  /** No track at all: the value is absent, and an empty track would lie. */
  Absent = "absent",
}

/** The journey graphic's three shapes. */
export enum CresultcardJourneyShape {
  Arc = "arc",
  Line = "line",
  Rung = "rung",
}

/** Where a journey's summary line gets its words. */
export enum CresultcardJourneySummarySource {
  /** The caller's own sentence. */
  Stated = "stated",
  /** No summary line at all. */
  Absent = "absent",
}

interface CresultcardJourneySummaryStated {
  readonly source: CresultcardJourneySummarySource.Stated;
  readonly text: string;
}

interface CresultcardJourneySummaryAbsent {
  readonly source: CresultcardJourneySummarySource.Absent;
  readonly text?: never;
}

/**
 * What a journey says beneath its graphic.
 *
 * OMITTED means the stops speak for themselves: the kit counts them and names
 * the ones the graphic could not draw. A journey with NO stops then says
 * nothing, because the only thing the kit could say there is that the trip was
 * uninterrupted — and every word for that ("direct", "nonstop") belongs to air
 * travel. Printed under a driving route it is not a summary of anything, which
 * is why the word is the caller's to supply and never the kit's to assume.
 */
export type CresultcardJourneySummarySpec =
  | CresultcardJourneySummaryStated
  | CresultcardJourneySummaryAbsent;

/** Whether a segmented control commits on arrow or on Enter. */
export enum CresultcardSegmentActivation {
  /** Cheap switches: moving the selection applies it. */
  Automatic = "automatic",
  /** Expensive switches: the arrow moves focus, Enter commits. */
  Manual = "manual",
}

/** The transport cluster's three sizes. */
export enum CresultcardTransportScale {
  Icon = "icon",
  Inline = "inline",
  Block = "block",
}

/** The three geometries one playback track is drawn in. */
export enum CresultcardProgressGeometry {
  /** A line under the content. */
  Line = "line",
  /** A ring around circular art, where the ring IS the track. */
  Ring = "ring",
  /** A fill running behind a row's or capsule's own content. */
  Bed = "bed",
}

/** What the one audio element is doing. */
export enum CresultcardPlaybackStatus {
  Idle = "idle",
  Buffering = "buffering",
  Playing = "playing",
  Paused = "paused",
  /**
   * The track ran out. Distinct from `Idle` because a finished track is an
   * event a reader has to be told about, and `Idle` is the absence of one.
   */
  Ended = "ended",
  Failed = "failed",
}

/** The equaliser is at rest or moving. Rest is a state, not an absence. */
export enum CresultcardEqualizerState {
  Idle = "idle",
  Active = "active",
}

/* ── Set state ────────────────────────────────────────────────────────────── */

/**
 * What the set has to show. The three non-ready faces are visually distinct
 * from each other on purpose: a reader must never have to guess whether a
 * surface is still working, came back empty, or failed.
 */
export enum CresultcardSetStatus {
  /** Items are present and rendered. */
  Ready = "ready",
  /** The lane is still answering. */
  Loading = "loading",
  /** The lane answered with nothing. */
  Empty = "empty",
  /** The lane did not answer. */
  Failed = "failed",
}

/* ── Shared slot shapes ───────────────────────────────────────────────────── */

/**
 * The item's ONE control, handed down by the set that owns the drill.
 *
 * It lives here rather than on the identity block because the set constructs it
 * and three renderers consume it: a composition binds it to whichever element
 * is the item's title, and that element becomes the item's only tab stop.
 */
export interface CresultcardOpenControl {
  readonly expanded: boolean;
  readonly paneId: string;
  readonly onOpen: () => void;
}

/**
 * One thing a reader can do. Exactly one of `href` / `onPress` is set, and the
 * union is what enforces "exactly one": with both optional on one shape, a lane
 * that publishes no destination could satisfy the type by declaring NEITHER,
 * and the only way to comply was to render an inert control that does nothing
 * when pressed. A lane with nothing to offer now omits the action instead.
 */
interface CresultcardActionSpecBase {
  readonly label: string;
  readonly tone: CresultcardActionTone;
  readonly icon?: TablerIcon;
  /** A price, a duration, a phone number — rendered inside the control. */
  readonly value?: ReactNode;
  readonly compact?: boolean;
  readonly fullWidth?: boolean;
}

/** A destination. It renders as a real anchor, with a real link menu. */
interface CresultcardActionHrefSpec extends CresultcardActionSpecBase {
  readonly href: string;
  readonly onPress?: never;
}

/** A behaviour on this surface. */
interface CresultcardActionPressSpec extends CresultcardActionSpecBase {
  readonly href?: never;
  readonly onPress: () => void;
}

export type CresultcardActionSpec =
  | CresultcardActionHrefSpec
  | CresultcardActionPressSpec;

/** One discrete value+unit fact. Every one of these must survive. */
export interface CresultcardFactSpec {
  readonly value: ReactNode;
  readonly unit?: string;
  readonly icon?: TablerIcon;
}

/** One label/value pair, with the caller's own judgement of its length. */
export interface CresultcardFactEntry {
  readonly label: string;
  readonly value: ReactNode;
  readonly weight: CresultcardFactWeight;
  readonly tone?: CresultcardTone;
}

/** One separated meta fact on an inline strip. */
export interface CresultcardFactLineEntry {
  readonly key: string;
  readonly value: ReactNode;
}

/** One labelled count in a terminal strip. */
export interface CresultcardStatSpec {
  readonly glyph?: TablerIcon;
  readonly value: ReactNode;
  readonly label: string;
  readonly tone?: CresultcardTone;
}

/** One option in a segmented control. A disabled option explains itself. */
export interface CresultcardSegmentOption {
  readonly id: string;
  readonly label: string;
  readonly enabled: boolean;
  readonly disabledReason?: string;
}
